import { Suspense, useCallback, useMemo, useState } from "react";
import * as React from "react";

import { DialogLoadErrorBoundary } from "components/dialog-load-error-boundary/dialog-load-error-boundary.component";
import DialogLoadingFallback from "components/dialog-loading-fallback";
import { useStickyTrue } from "hooks/use-sticky-true.hook";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

import { Patient } from "../services/types/patients.type";

type EmailProviderProps = {
  children?: React.ReactNode;
};

const EmailForm = lazyWithRetry(
  () => import("components/email-form/email-form.component"),
  "email-form"
);

export type OpenEmailArgs = {
  patient?: Patient;
  emailContent?: string;
  emailSubject?: string;
  noSignature?: boolean;
  /**
   * Paint the form above a host dialog that raised its own z-index. Omitted (the default) keeps
   * MUI's `theme.zIndex.modal` (1300), which is what every non-nested caller wants. The Ambient
   * Scribe details modal sits at 1501, so a form opened from inside it renders underneath without
   * this — see `getScribeModalNestedDialogZIndex`.
   */
  zIndex?: number;
  /**
   * The generated scribe note this send delivers, recorded server-side so the letter can report
   * that it was emailed and a preview can show what went out.
   *
   * Only the After Visit Summary surfaces pass it, and those are behind the `afterVisitSummary`
   * flag — which is what keeps the new `ambient_scribe_note` write key off requests to a backend
   * that predates it (`CustomModelSerializer` 400s an unknown key).
   */
  ambientScribeNoteId?: number;
  /**
   * Box files to attach for this one open, in place of the shared `selectedFiles`. The E-Documents
   * "Email Multiple Files" picker passes its choice here so it never overwrites the explorer's own
   * selection, which drives "Email Selected File". Omitted, the form attaches `selectedFiles`.
   */
  attachments?: any[];
};

type EmailContextValue = {
  handleOpen: (args?: OpenEmailArgs) => void;
  selectedFiles: any[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<any[]>>;
  setInitialDocuments: React.Dispatch<React.SetStateAction<File[]>>;
  initialDocuments: File[];
  initialEmailContent: string;
};

const EmailContext = React.createContext<EmailContextValue | null>(null);

export function EmailProvider({ children }: EmailProviderProps) {
  const [openModal, setOpenModal] = useState(false);
  const shouldMountEmailForm = useStickyTrue(openModal);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [initialDocuments, setInitialDocuments] = useState<File[]>([]);
  const [initialEmailContent, setInitialEmailContent] = useState<string>("");
  const [initialSubject, setInitialSubject] = useState<string>("");
  const [noSignature, setNoSignature] = useState<boolean>(false);
  const [zIndex, setZIndex] = useState<number | undefined>(undefined);
  const [ambientScribeNoteId, setAmbientScribeNoteId] = useState<
    number | undefined
  >(undefined);
  const [openAttachments, setOpenAttachments] = useState<any[] | null>(null);

  const handleOpen = ({
    patient: patientArg,
    emailContent,
    emailSubject,
    noSignature: noSignatureArg,
    zIndex: zIndexArg,
    ambientScribeNoteId: ambientScribeNoteIdArg,
    attachments
  }: OpenEmailArgs = {}) => {
    setPatient(patientArg || null);
    setOpenModal(true);
    setInitialEmailContent(emailContent ?? "");
    setInitialSubject(emailSubject ?? "");
    setNoSignature(noSignatureArg ?? false);
    setZIndex(zIndexArg);
    setAmbientScribeNoteId(ambientScribeNoteIdArg);
    // Set on every open (null when not passed) and left alone on close, so the attachment list
    // does not change during the close transition and never leaks into the next caller's open.
    setOpenAttachments(attachments ?? null);
  };

  // The form's per-file remove button edits a copy for this open only. Removing from the shared
  // `selectedFiles` would empty the E-Documents explorer's selection behind the user's back and
  // disable "Email Selected File" while the row still shows as selected.
  const setAttachmentsForThisOpen = useCallback<
    React.Dispatch<React.SetStateAction<any[]>>
  >(
    (update) =>
      setOpenAttachments((current) => {
        const base = current ?? selectedFiles;
        return typeof update === "function" ? update(base) : update;
      }),
    [selectedFiles]
  );

  const handleClose = () => {
    setOpenModal(false);
    setPatient(null);
    setInitialDocuments([]);
    setInitialEmailContent("");
    setInitialSubject("");
    setNoSignature(false);
    setZIndex(undefined);
    setAmbientScribeNoteId(undefined);
  };

  const value = useMemo<EmailContextValue>(() => {
    return {
      handleOpen,
      selectedFiles,
      setSelectedFiles,
      setInitialDocuments,
      initialDocuments,
      initialEmailContent
    };
  }, [initialDocuments, initialEmailContent, selectedFiles]);
  return (
    <EmailContext.Provider value={value}>
      {children}
      {shouldMountEmailForm && (
        <DialogLoadErrorBoundary dialogName="email-form">
          <Suspense fallback={<DialogLoadingFallback />}>
            <EmailForm
              isOpen={openModal}
              onClose={handleClose}
              patient={patient || undefined}
              selectedFiles={openAttachments ?? selectedFiles}
              setSelectedFiles={setAttachmentsForThisOpen}
              initialDocuments={initialDocuments}
              initialEmailContent={initialEmailContent}
              initialSubject={initialSubject}
              noSignature={noSignature}
              zIndex={zIndex}
              ambientScribeNoteId={ambientScribeNoteId}
            />
          </Suspense>
        </DialogLoadErrorBoundary>
      )}
    </EmailContext.Provider>
  );
}

export default function useEmail() {
  const emailContext = React.useContext(EmailContext);
  if (!emailContext) {
    throw new Error(`useEmail must be used within a EmailProvider`);
  }

  return emailContext;
}
